import { useCallback } from "react";

import { queryClient } from "../../../../services/query";

import { useMaybeUserId } from "../../userId";
import { useMaybeFabricFeatureFlag } from "../../featureFlags";

import {
  operationQueryKeyPrefix,
  computeOperationQueryKey,
} from "./operation.query";

// ############################
// ## useInvalidateOperation ##
// ############################

export type ResultOfUseInvalidateOperation = (
  operationId: string,
) => Promise<void>;
export type UseInvalidateOperation = () => ResultOfUseInvalidateOperation;

/**
 * Invalidates the operation data of the given operationId.
 *
 * A fresh fetch of data will be initiated when a hook instance is active that uses this data.
 */
export const useInvalidateOperation: UseInvalidateOperation = () => {
  const userId = useMaybeUserId();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });
  return useCallback(
    async (operationId: string) => {
      const queryKey = computeOperationQueryKey({
        userId,
        operationId,
        catalogFlag,
      });
      await queryClient.invalidateQueries(queryKey);
    },
    [userId, catalogFlag],
  );
};

// #################################
// ## useInvalidateEveryOperation ##
// #################################

export type ResultOfUseInvalidateEveryOperation = () => Promise<void>;
export type UseInvalidateEveryOperation =
  () => ResultOfUseInvalidateEveryOperation;

/**
 * Invalidates every operation data, regardless of operationId.
 *
 * A fresh fetch of data will be initiated when a hook instance is active that uses this data.
 */
export const useInvalidateEveryOperation: UseInvalidateEveryOperation = () => {
  return useCallback(async () => {
    await queryClient.invalidateQueries(operationQueryKeyPrefix);
  }, []);
};

// #######################
// ## useClearOperation ##
// #######################

export type ResultOfUseClearOperation = (operationId: string) => Promise<void>;
export type UseClearOperation = () => ResultOfUseClearOperation;

/**
 * Clears and invalidates the operation data of the given operationId.
 *
 * A fresh fetch of data will be initiated when a hook instance is active that uses this data.
 */
export const useClearOperation: UseClearOperation = () => {
  const userId = useMaybeUserId();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });
  return useCallback(
    async (operationId) => {
      const queryKey = computeOperationQueryKey({
        userId,
        operationId,
        catalogFlag,
      });
      await queryClient.resetQueries(queryKey);
    },
    [userId, catalogFlag],
  );
};

// ############################
// ## useClearEveryOperation ##
// ############################

export type ResultOfUseClearEveryOperation = () => Promise<void>;
export type UseClearEveryOperation = () => ResultOfUseClearEveryOperation;

/**
 * Clears and invalidates every operation data, regardless of operationId.
 *
 * A fresh fetch of data will be initiated when a hook instance is active that uses this data.
 */
export const useClearEveryOperation: UseClearEveryOperation = () => {
  return useCallback(async () => {
    await queryClient.resetQueries(operationQueryKeyPrefix);
  }, []);
};
